import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, Globe, ListChecks, LogOut, Moon, Plus, ShoppingBasket, Sun, UserRound, WifiOff } from 'lucide-react';
import { LANGUAGES } from '@dishly/shared';
import { logOut, updatePreferences } from '../lib/account';
import { useOnline } from '../lib/browser';
import { loginPath } from '../lib/navigation';
import { useListOfKind } from '../stores/data';
import { resolvedTheme, usePrefs } from '../stores/prefs';
import { useSession } from '../stores/session';
import { LogoMark } from './Logo';
import { Menu, MenuItem } from './Menu';

function ThemeToggle() {
  const { t } = useTranslation();
  const theme = usePrefs((s) => resolvedTheme(s.theme));
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className="icon-btn"
      onClick={() => usePrefs.setState({ theme: next })}
      aria-label={t(next === 'dark' ? 'nav.themeDark' : 'nav.themeLight')}
      title={t(next === 'dark' ? 'nav.themeDark' : 'nav.themeLight')}
    >
      {theme === 'dark' ? <Sun className="size-5" /> : <Moon className="size-5" />}
    </button>
  );
}

function LanguageMenu() {
  const { t } = useTranslation();
  const language = usePrefs((s) => s.language);
  return (
    <Menu
      trigger={(props) => (
        <button type="button" className="icon-btn gap-1 sm:w-auto sm:px-2.5" aria-label={t('nav.language')} title={t('nav.language')} {...props}>
          <Globe className="size-5" />
          <span className="hidden text-xs font-bold uppercase sm:inline">{language}</span>
        </button>
      )}
    >
      {(close) =>
        LANGUAGES.map((l) => (
          <MenuItem
            key={l.code}
            active={l.code === language}
            onSelect={() => {
              void updatePreferences({ language: l.code });
              close();
            }}
          >
            <span lang={l.code} className="flex-1">
              {l.name}
            </span>
            {l.code === language && <Check className="size-4" />}
          </MenuItem>
        ))
      }
    </Menu>
  );
}

function UserMenu({ name }: { name: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Menu
      trigger={(props) => (
        <button
          type="button"
          className="flex size-10 items-center justify-center rounded-full bg-brand-soft font-bold text-brand transition-colors hover:bg-brand hover:text-on-brand"
          aria-label={t('nav.userMenu')}
          title={name}
          {...props}
        >
          {name.trim().charAt(0).toUpperCase() || <UserRound className="size-5" />}
        </button>
      )}
    >
      {(close) => (
        <>
          <p className="truncate px-3 pt-1.5 pb-2 text-sm font-semibold">{name}</p>
          <MenuItem onSelect={() => (close(), void navigate('/my-recipes'))}>
            <BookOpen className="size-4" /> {t('nav.myRecipes')}
          </MenuItem>
          <MenuItem onSelect={() => (close(), void navigate('/lists'))}>
            <ListChecks className="size-4" /> {t('nav.lists')}
          </MenuItem>
          <MenuItem onSelect={() => (close(), void navigate('/account'))}>
            <UserRound className="size-4" /> {t('nav.account')}
          </MenuItem>
          <MenuItem
            onSelect={() => {
              close();
              void logOut().then(() => navigate('/'));
            }}
          >
            <LogOut className="size-4" /> {t('nav.logOut')}
          </MenuItem>
        </>
      )}
    </Menu>
  );
}

/** Site header. Deliberately not sticky: it scrolls away with the page. */
export function Header() {
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const online = useOnline();
  const shopping = useListOfKind('shopping');
  const shoppingCount = shopping?.items.length ?? 0;

  return (
    <header className="border-b border-line bg-surface">
      <div className="mx-auto flex max-w-7xl items-center gap-1 px-3 py-2.5 sm:gap-2 sm:px-6">
        <Link to="/" className="me-auto flex items-center gap-2 rounded-xl pe-2" aria-label={t('nav.home')}>
          <LogoMark />
          <span className="font-display text-xl font-bold tracking-tight sm:text-2xl">{t('app.name')}</span>
        </Link>

        {!online && (
          <span className="hidden items-center gap-1.5 rounded-full bg-surface-2 px-3 py-1 text-xs font-semibold text-muted sm:inline-flex" title={t('nav.offlineHint')}>
            <WifiOff className="size-3.5" /> {t('nav.offline')}
          </span>
        )}

        <Link to={user ? '/new' : loginPath('/new')} className="btn-primary px-3 sm:px-4" aria-label={t('nav.addRecipe')}>
          <Plus className="size-5" />
          <span className="hidden sm:inline">{t('nav.add')}</span>
        </Link>

        <Link
          to={user ? '/lists/shopping' : loginPath('/lists/shopping')}
          className="icon-btn relative"
          aria-label={t('nav.shoppingList')}
          title={t('nav.shoppingList')}
        >
          <ShoppingBasket className="size-5" />
          {shoppingCount > 0 && (
            <span className="absolute -end-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-[11px] font-bold text-on-brand">
              {shoppingCount}
            </span>
          )}
        </Link>

        <ThemeToggle />
        <LanguageMenu />

        {user ? (
          <UserMenu name={user.displayName} />
        ) : (
          <Link to="/login" className="btn-secondary ms-1 px-3 sm:px-4">
            <UserRound className="size-4 sm:hidden" />
            <span className="sr-only sm:not-sr-only">{t('nav.logIn')}</span>
          </Link>
        )}
      </div>
      {!online && (
        <p className="border-t border-line bg-surface-2 px-4 py-1.5 text-center text-xs text-muted sm:hidden">
          <WifiOff className="me-1 inline size-3.5" /> {t('nav.offlineHint')}
        </p>
      )}
    </header>
  );
}
